//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import React, { createContext, useContext, useEffect, useState } from 'react';

type DarkModeContextType = {
  isDarkMode: boolean;
  toggleMode: () => void;
  setMode: (mode: 'dark' | 'light') => void;
};

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

const getSystemTheme = () => (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

export const DarkModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 初始化
  const [mode, setModeState] = useState<'dark' | 'light'>(() => {
    const local = localStorage.getItem('darkMode');
    if (local === 'dark' || local === 'light') return local;
    return getSystemTheme();
  });

  // 寫入 localStorage、data-theme、class
  useEffect(() => {
    localStorage.setItem('darkMode', mode);
    // daisyUI
    document.documentElement.setAttribute('data-theme', mode === 'dark' ? 'night' : 'winter');
    // Tailwind
    document.documentElement.classList.toggle('dark', mode === 'dark');
  }, [mode]);

  // 監聽系統主題變化（localStorage 無設定時才跟隨系統）
  useEffect(() => {
    const local = localStorage.getItem('darkMode');
    if (local) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setModeState(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const setMode = (newMode: 'dark' | 'light') => setModeState(newMode);
  const toggleMode = () => setModeState((prev) => (prev === 'dark' ? 'light' : 'dark'));

  return (
    <DarkModeContext.Provider
      value={{
        isDarkMode: mode === 'dark',
        toggleMode,
        setMode,
      }}
    >
      {children}
    </DarkModeContext.Provider>
  );
};

export function useDarkModeContext() {
  const ctx = useContext(DarkModeContext);
  if (!ctx) throw new Error('useDarkModeContext must be used within DarkModeProvider');
  return ctx;
}
